import React, {useEffect, useRef} from 'react'
import ApexCharts from 'apexcharts'
import {ChartConfig} from '../types'

interface ReportChartProps {
  config: ChartConfig
  data: Array<Record<string, any>>
}

const ReportChart: React.FC<ReportChartProps> = ({config, data}) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<ApexCharts | null>(null)

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy()
      chartInstance.current = null
    }

    if (!chartRef.current || !config.enabled || data.length === 0) return

    const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark'
    const labelColor = isDark ? '#cdcdde' : undefined
    const seriesFields = config.seriesFields || [config.yAxisField]
    const seriesLabels = config.seriesLabels || seriesFields
    const colors = config.colors || ['#009EF7', '#50CD89', '#F1416C', '#FFC700', '#7239EA']
    const isPieOrDonut = config.chartType === 'pie' || config.chartType === 'donut'

    const categories = data.map((row) => {
      if (config.xAxisFields && config.xAxisFields.length > 0) {
        return config.xAxisFields.map((f) => String(row[f] || '')).join(' - ')
      }
      const value = row[config.xAxisField]
      if (
        value instanceof Date ||
        (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value))
      ) {
        return new Date(value).toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
        })
      }
      return String(value)
    })

    let options: ApexCharts.ApexOptions

    if (isPieOrDonut) {
      const pieField = seriesFields[0]
      options = {
        series: data.map((row) => Number(row[pieField] || 0)),
        labels: categories,
        chart: {
          type: config.chartType as any,
          height: config.height,
          fontFamily: 'Poppins, sans-serif',
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
          animations: {
            enabled: true,
            speed: 500,
          },
        },
        colors,
        dataLabels: {
          enabled: config.showDataLabels,
        },
        legend: {
          show: config.showLegend,
          position: 'bottom',
          horizontalAlign: 'center',
          labels: {colors: labelColor},
        },
        tooltip: {
          theme: isDark ? 'dark' : 'light',
          y: {
            formatter: (val) => {
              if (typeof val === 'number') {
                return new Intl.NumberFormat('en-IN', {
                  maximumFractionDigits: 2,
                }).format(val)
              }
              return String(val)
            },
          },
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: config.chartType === 'donut',
                value: {color: labelColor},
                name: {color: labelColor},
                total: {
                  show: true,
                  label: 'Total',
                  color: labelColor,
                  formatter: (w) => {
                    const total = w.globals.seriesTotals.reduce(
                      (a: number, b: number) => a + b,
                      0
                    )
                    return new Intl.NumberFormat('en-IN').format(total)
                  },
                },
              },
            },
          },
        },
      }
    } else {
      const series = seriesFields.map((field, index) => ({
        name: seriesLabels[index] || field,
        data: data.map((row) => Number(row[field] || 0)),
      }))

      options = {
        series,
        chart: {
          type: config.chartType as any,
          height: config.height,
          fontFamily: 'Poppins, sans-serif',
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false,
            },
          },
          animations: {
            enabled: true,
            speed: 500,
          },
        },
        colors,
        dataLabels: {
          enabled: config.showDataLabels,
        },
        stroke: {
          curve: 'smooth',
          width: config.chartType === 'area' || config.chartType === 'line' ? 3 : 0,
        },
        fill: {
          type: config.chartType === 'area' ? 'gradient' : 'solid',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, 90, 100],
          },
        },
        xaxis: {
          categories,
          title: {
            text: config.xAxisLabel,
            style: {fontSize: '12px', fontWeight: 500, color: labelColor},
          },
          labels: {
            style: {fontSize: '11px', colors: labelColor},
            rotate: -45,
            rotateAlways: categories.length > 10,
          },
        },
        yaxis: {
          title: {
            text: config.yAxisLabel,
            style: {fontSize: '12px', fontWeight: 500, color: labelColor},
          },
          labels: {
            style: {colors: labelColor ? [labelColor] : undefined},
            formatter: (val) => {
              if (typeof val === 'number') {
                return new Intl.NumberFormat('en-IN', {
                  maximumFractionDigits: 0,
                  notation: val > 100000 ? 'compact' : 'standard',
                }).format(val)
              }
              return String(val)
            },
          },
        },
        legend: {
          show: config.showLegend,
          position: 'top',
          horizontalAlign: 'right',
          labels: {colors: labelColor},
        },
        tooltip: {
          theme: isDark ? 'dark' : 'light',
          y: {
            formatter: (val) => {
              if (typeof val === 'number') {
                return new Intl.NumberFormat('en-IN', {
                  maximumFractionDigits: 2,
                }).format(val)
              }
              return String(val)
            },
          },
        },
        grid: {
          borderColor: isDark ? '#2B2B40' : '#e0e0e0',
          strokeDashArray: 4,
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: '60%',
          },
        },
      }
    }

    chartInstance.current = new ApexCharts(chartRef.current, options)
    chartInstance.current.render()

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
        chartInstance.current = null
      }
    }
  }, [config, data])

  if (!config.enabled) return null

  return (
    <div className='card mb-5 d-print-none'>
      <div className='card-body'>
        <div ref={chartRef} style={{height: config.height}}></div>
      </div>
    </div>
  )
}

export default ReportChart
